import { assertNever } from "@redotech/util/type";
import { ObjectId } from "bson";
import { ButtonHierarchy } from "../content-builder/element-schema";
import {
  ButtonSettings,
  DEFAULT_BLOCK_SIZES,
  GameType,
  OrderLookupSettings,
  PageButtonSettings,
  PromotionType,
  SubscriptionUpsellFallbackCondition,
  TrackingPageBlock,
  TrackingPageBlockType,
  TrackingPageCleanupCrewGameBlock,
  TrackingPageDeliveryDashGameBlock,
  TrackingPageFlappyFreightGameBlock,
  TrackingPageGalagaGameBlock,
  TrackingPageGameBlock,
  TrackingPageHeaderBlock,
  TrackingPageItemsInFulfillmentBlock,
  TrackingPageProductUpsellBlock,
  TrackingPagePromotionBlock,
  TrackingPageSettings,
  TrackingPageSubscriptionUpsellBlock,
  TrackingPageTinyPackageGameBlock,
  TrackingPageTrackingDetailsBlock,
  TrackingPageTrackingUpdatesBlock,
} from "./tracking-page-definition";

export const defaultPrimaryButtonConfiguration = {
  default: {
    style: { background: "#141414", border: "#525252" },
    text: { fontColor: "#ffffff" },
  },
  hovered: {
    style: { background: "#424242", border: "#525252" },
    text: { fontColor: "#ffffff" },
  },
  active: {
    style: { background: "#737373", border: "#525252" },
    text: { fontColor: "#ffffff" },
  },
};

export const defaultSecondaryButtonConfiguration = {
  default: {
    style: { background: "#ffffff", border: "#d6d6d6" },
    text: { fontColor: "#424242" },
  },
  hovered: {
    style: { background: "#f5f5f5", border: "#d6d6d6" },
    text: { fontColor: "#424242" },
  },
  active: {
    style: { background: "#e5e5e5", border: "#d6d6d6" },
    text: { fontColor: "#424242" },
  },
};

export const defaultTertiaryButtonConfiguration = {
  default: {
    style: { background: "transparent", border: "transparent" },
    text: { fontColor: "#424242" },
  },
  hovered: {
    style: { background: "#f5f5f5", border: "transparent" },
    text: { fontColor: "#424242" },
  },
  active: {
    style: { background: "#e5e5e5", border: "transparent" },
    text: { fontColor: "#424242" },
  },
};

export function trackingPagePageButtonSettingsDefault(): PageButtonSettings {
  return {
    primary: defaultPrimaryButtonConfiguration,
    secondary: defaultSecondaryButtonConfiguration,
    tertiary: defaultTertiaryButtonConfiguration,
    palette: {
      baseColor: "627d98",
      valueStop: 500,
      colorSwatches: {
        50: "#f0f4f8",
        100: "#d9e2ec",
        200: "#bcccdc",
        300: "#9fb3c8",
        400: "#829ab1",
        500: "#627d98",
        600: "#486581",
        700: "#334e68",
        800: "#243b53",
        900: "#102a43",
        950: "#061a2b",
      },
    },
  };
}

export function trackingPageButtonSettingsDefault(
  hierarchy: ButtonHierarchy,
): ButtonSettings {
  switch (hierarchy) {
    case "primary":
      return {
        hierarchy: "primary",
        buttonConfiguration: defaultPrimaryButtonConfiguration,
      };
    case "secondary":
      return {
        hierarchy: "secondary",
        buttonConfiguration: defaultSecondaryButtonConfiguration,
      };
    case "tertiary":
      return {
        hierarchy: "tertiary",
        buttonConfiguration: defaultTertiaryButtonConfiguration,
      };
    default:
      assertNever(hierarchy);
  }
}

export function trackingPageHeaderBlockDefault(options?: {
  _id?: string;
  anchorCoordinates?: { x: number; y: number };
}): TrackingPageHeaderBlock {
  const size = DEFAULT_BLOCK_SIZES[TrackingPageBlockType.HEADER];
  return {
    _id: options?._id ?? new ObjectId().toString(),
    type: TrackingPageBlockType.HEADER,
    anchorCoordinates: options?.anchorCoordinates ?? { x: 0, y: 0 },
    size,
  };
}

export function trackingPageTrackingDetailsBlockDefault(options?: {
  _id?: string;
  anchorCoordinates?: { x: number; y: number };
}): TrackingPageTrackingDetailsBlock {
  const size = DEFAULT_BLOCK_SIZES[TrackingPageBlockType.TRACKING_DETAILS];
  return {
    _id: options?._id ?? new ObjectId().toString(),
    type: TrackingPageBlockType.TRACKING_DETAILS,
    anchorCoordinates: options?.anchorCoordinates ?? { x: 0, y: 0 },
    size,
  };
}

export function trackingPageItemsInFulfillmentBlockDefault(options?: {
  _id?: string;
  anchorCoordinates?: { x: number; y: number };
}): TrackingPageItemsInFulfillmentBlock {
  const size = DEFAULT_BLOCK_SIZES[TrackingPageBlockType.ITEMS_IN_FULFILLMENT];
  return {
    _id: options?._id ?? new ObjectId().toString(),
    type: TrackingPageBlockType.ITEMS_IN_FULFILLMENT,
    anchorCoordinates: options?.anchorCoordinates ?? { x: 0, y: 3 },
    size,
    imageAspectRatio: 1,
    buttonSettings: trackingPageButtonSettingsDefault("secondary"),
  };
}

export function trackingPageProductUpsellBlockDefault(options?: {
  _id?: string;
  anchorCoordinates?: { x: number; y: number };
}): TrackingPageProductUpsellBlock {
  const size = DEFAULT_BLOCK_SIZES[TrackingPageBlockType.PRODUCT_UPSELL];
  return {
    _id: options?._id ?? new ObjectId().toString(),
    type: TrackingPageBlockType.PRODUCT_UPSELL,
    anchorCoordinates: options?.anchorCoordinates ?? { x: 2, y: 0 },
    size,
    imageAspectRatio: 1,
    checkoutButtonSettings: trackingPageButtonSettingsDefault("secondary"),
    dropdownSettings: trackingPageButtonSettingsDefault("secondary"),
  };
}

export function trackingPagePromotionBlockDefault(options?: {
  _id?: string;
  anchorCoordinates?: { x: number; y: number };
}): TrackingPagePromotionBlock {
  const size = DEFAULT_BLOCK_SIZES[TrackingPageBlockType.PROMOTION];
  return {
    _id: options?._id ?? new ObjectId().toString(),
    type: TrackingPageBlockType.PROMOTION,
    anchorCoordinates: options?.anchorCoordinates ?? { x: 5, y: 0 },
    size,
    promotionType: PromotionType.PAGE,
  };
}

export function trackingPageTrackingUpdatesBlockDefault(options?: {
  _id?: string;
  anchorCoordinates?: { x: number; y: number };
}): TrackingPageTrackingUpdatesBlock {
  const size = DEFAULT_BLOCK_SIZES[TrackingPageBlockType.TRACKING_UPDATES];
  return {
    _id: options?._id ?? new ObjectId().toString(),
    type: TrackingPageBlockType.TRACKING_UPDATES,
    anchorCoordinates: options?.anchorCoordinates ?? { x: 0, y: 0 },
    size,
    title: "Subscribe to tracking updates",
    buttonSettings: trackingPageButtonSettingsDefault("primary"),
  };
}

export function trackingPageSubscriptionUpsellBlockDefault(options?: {
  _id?: string;
  anchorCoordinates?: { x: number; y: number };
}): TrackingPageSubscriptionUpsellBlock {
  const size = DEFAULT_BLOCK_SIZES[TrackingPageBlockType.SUBSCRIPTION_UPSELL];
  return {
    _id: options?._id ?? new ObjectId().toString(),
    type: TrackingPageBlockType.SUBSCRIPTION_UPSELL,
    anchorCoordinates: options?.anchorCoordinates ?? { x: 0, y: 6 },
    size,
    fallbackCondition:
      SubscriptionUpsellFallbackCondition.CUSTOMER_IS_SUBSCRIBED,
    fallbackBlock: TrackingPageBlockType.PROMOTION,
    fallbackBlockSettings: trackingPagePromotionBlockDefault(),
    title: "Start your recurring subscription!",
    perks: ["Free shipping", "Free returns", "Priority access"],
    buttonSettings: trackingPageButtonSettingsDefault("primary"),
    secondaryButtonSettings: trackingPageButtonSettingsDefault("secondary"),
    dropdownSettings: trackingPageButtonSettingsDefault("secondary"),
  };
}

export function trackingPageGalagaGameBlockDefault(options?: {
  _id?: string;
  anchorCoordinates?: { x: number; y: number };
}): TrackingPageGalagaGameBlock {
  const size = DEFAULT_BLOCK_SIZES[TrackingPageBlockType.GAME];
  return {
    _id: options?._id ?? new ObjectId().toString(),
    type: TrackingPageBlockType.GAME,
    anchorCoordinates: options?.anchorCoordinates ?? { x: 0, y: 6 },
    size,
    gameType: GameType.GALAGA,
    difficulty: "easy",
  };
}

export function trackingPageCleanupCrewGameBlockDefault(options?: {
  _id?: string;
  anchorCoordinates?: { x: number; y: number };
}): TrackingPageCleanupCrewGameBlock {
  const size = DEFAULT_BLOCK_SIZES[TrackingPageBlockType.GAME];
  return {
    _id: options?._id ?? new ObjectId().toString(),
    type: TrackingPageBlockType.GAME,
    anchorCoordinates: options?.anchorCoordinates ?? { x: 0, y: 6 },
    size,
    gameType: GameType.CLEANUP_CREW,
    gameSettings: {
      selectedLevel: 0, // Default to first level
      useRandomLevel: true, // Default to random level selection
    },
    rewardSettings: {
      showRewardOnVictory: false,
      rewardMessage: "You've earned a discount!",
    },
  };
}

export function trackingPageDeliveryDashGameBlockDefault(options?: {
  _id?: string;
  anchorCoordinates?: { x: number; y: number };
}): TrackingPageDeliveryDashGameBlock {
  const size = DEFAULT_BLOCK_SIZES[TrackingPageBlockType.GAME];
  return {
    _id: options?._id ?? new ObjectId().toString(),
    type: TrackingPageBlockType.GAME,
    anchorCoordinates: options?.anchorCoordinates ?? { x: 0, y: 6 },
    size,
    gameType: GameType.DELIVERY_DASH,
    rewardSettings: {
      showRewardOnVictory: false,
      rewardMessage: "You've earned a discount!",
    },
  };
}

export function trackingPageFlappyFreightGameBlockDefault(options?: {
  _id?: string;
  anchorCoordinates?: { x: number; y: number };
}): TrackingPageFlappyFreightGameBlock {
  const size = DEFAULT_BLOCK_SIZES[TrackingPageBlockType.GAME];
  return {
    _id: options?._id ?? new ObjectId().toString(),
    type: TrackingPageBlockType.GAME,
    anchorCoordinates: options?.anchorCoordinates ?? { x: 0, y: 6 },
    size,
    gameType: GameType.FLAPPY_FREIGHT,
    gameSettings: null,
    rewardSettings: {
      showRewardOnVictory: false,
      rewardMessage: "You've earned a discount!",
    },
  };
}

export function trackingPageTinyPackageGameBlockDefault(options?: {
  _id?: string;
  anchorCoordinates?: { x: number; y: number };
}): TrackingPageTinyPackageGameBlock {
  const size = DEFAULT_BLOCK_SIZES[TrackingPageBlockType.GAME];
  return {
    _id: options?._id ?? new ObjectId().toString(),
    type: TrackingPageBlockType.GAME,
    anchorCoordinates: options?.anchorCoordinates ?? { x: 0, y: 6 },
    size,
    gameType: GameType.TINY_PACKAGE,
    gameSettings: {
      difficulty: "medium",
      playerSpriteUrl: undefined,
      playerSpriteSize: 32,
      npcSpriteUrl: undefined,
      npcSpriteSize: 32,
    },
    rewardSettings: {
      showRewardOnVictory: false,
      rewardMessage: "You've earned a discount!",
    },
  };
}

export function trackingPageGameBlockDefault(options?: {
  _id?: string;
  anchorCoordinates?: { x: number; y: number };
  gameType?: GameType;
}): TrackingPageGameBlock {
  const gameType = options?.gameType ?? GameType.DELIVERY_DASH;
  switch (gameType) {
    case GameType.GALAGA:
      return trackingPageGalagaGameBlockDefault(options);
    case GameType.CLEANUP_CREW:
      return trackingPageCleanupCrewGameBlockDefault(options);
    case GameType.DELIVERY_DASH:
      return trackingPageDeliveryDashGameBlockDefault(options);
    case GameType.FLAPPY_FREIGHT:
      return trackingPageFlappyFreightGameBlockDefault(options);
    case GameType.TINY_PACKAGE:
      return trackingPageTinyPackageGameBlockDefault(options);
    default:
      assertNever(gameType);
  }
}

export function getDefaultTrackingPageBlockOfType(
  blockType: TrackingPageBlockType,
  options?: { _id?: string; anchorCoordinates?: { x: number; y: number } },
): TrackingPageBlock {
  switch (blockType) {
    case TrackingPageBlockType.HEADER:
      return trackingPageHeaderBlockDefault(options);
    case TrackingPageBlockType.TRACKING_DETAILS:
      return trackingPageTrackingDetailsBlockDefault(options);
    case TrackingPageBlockType.ITEMS_IN_FULFILLMENT:
      return trackingPageItemsInFulfillmentBlockDefault(options);
    case TrackingPageBlockType.PRODUCT_UPSELL:
      return trackingPageProductUpsellBlockDefault(options);
    case TrackingPageBlockType.PROMOTION:
      return trackingPagePromotionBlockDefault(options);
    case TrackingPageBlockType.GAME:
      return trackingPageGameBlockDefault(options);
    case TrackingPageBlockType.SUBSCRIPTION_UPSELL:
      return trackingPageSubscriptionUpsellBlockDefault(options);
    case TrackingPageBlockType.TRACKING_UPDATES:
      return trackingPageTrackingUpdatesBlockDefault(options);
    default:
      assertNever(blockType);
  }
}

export function defaultOrderLookupSettings(): OrderLookupSettings {
  return {
    backgroundColor: "#F1F5F9",
    cardBackgroundColor: "#FFFFFF",
    cardBorderRadius: 0,
    titleText: "Track your order",
    titleColor: "#0F172A",
    buttonText: "Track order",
  };
}

export function createDefaultTrackingPageSettings(): TrackingPageSettings {
  return {
    name: "Untitled Tracking Page",
    blocks: [
      trackingPageTrackingDetailsBlockDefault(),
      trackingPageItemsInFulfillmentBlockDefault(),
      trackingPageProductUpsellBlockDefault(),
      trackingPagePromotionBlockDefault(),
    ],
    background: { backgroundColor: "#f5f5f5" },
    typography: {
      headerFontFamily: "Arial",
      headerFontColor: "#000000",
      bodyFontFamily: "Arial",
      bodyFontColor: "#000000",
      linkFontColor: "#3e28ad",
    },
    blockSettings: {
      backgroundColor: "#ffffff",
      borderRadius: 15,
      borderColor: "#d6d6d6",
    },
    buttonBorderRadius: 8,
    pageButtonSettings: trackingPagePageButtonSettingsDefault(),
  };
}
